import React from 'react';
import { User, Briefcase, FileText, Landmark, CheckCircle2, ChevronRight, Lock } from 'lucide-react';
import { Button } from './ui/Button';
interface Section {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
}
interface SectionsOverviewProps {
  completedSections: string[];
  onStartSection: (sectionId: string) => void;
}
const SECTIONS: Section[] = [{
  id: 'personal-info',
  title: 'A Bit About You',
  description: 'Personal details and identity verification',
  icon: User
}, {
  id: 'business-info',
  title: 'A Bit About Your Business',
  description: 'Company details and structure',
  icon: Briefcase
}, {
  id: 'documents',
  title: 'Supporting Documents',
  description: 'Upload required legal documentation',
  icon: FileText
}, {
  id: 'bank-account',
  title: 'Link Bank Account',
  description: 'Connect your primary business account',
  icon: Landmark
}, {
  id: 'review',
  title: 'Review',
  description: 'Final check before submission',
  icon: CheckCircle2
}];
export function SectionsOverview({
  completedSections,
  onStartSection
}: SectionsOverviewProps) {
  // Helper to check if a section is accessible
  // For this flow, we might enforce sequential order, or allow jumping.
  // Let's enforce sequential for a guided feel, but allow re-entering completed ones (optional).
  // The prompt implies "greyed out - explaining this section is complete", so maybe we can't re-enter?
  // "go back to sections screen where we'll show first section A Bit About you greyed out"
  const isSectionComplete = (id: string) => completedSections.includes(id);
  // Logic: A section is unlocked if the previous one is complete. First one is always unlocked.
  const isSectionUnlocked = (index: number) => {
    if (index === 0) return true;
    const prevSectionId = SECTIONS[index - 1].id;
    return isSectionComplete(prevSectionId);
  };
  return <div className="max-w-xl mx-auto py-8 animate-in fade-in duration-700" data-id="element-464">
      <div className="mb-8" data-id="element-465">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-466">
          Let's get you set up
        </h1>
        <p className="text-gray-600" data-id="element-467">
          Please complete the following sections to verify your account.
        </p>
      </div>

      <div className="space-y-4" data-id="element-468">
        {SECTIONS.map((section, index) => {
        const isComplete = isSectionComplete(section.id);
        const isUnlocked = isSectionUnlocked(index);
        const Icon = section.icon;
        return <div key={section.id} className={`
                relative overflow-hidden rounded-xl border transition-all duration-300
                ${isComplete ? 'bg-gray-50 border-gray-200 opacity-75' : isUnlocked ? 'bg-white border-gray-200 shadow-sm hover:border-blue-300 hover:shadow-md' : 'bg-gray-50 border-gray-100 opacity-60'}
              `} data-id="element-469">
              <div className="p-5 flex items-center gap-4" data-id="element-470">
                {/* Icon Container */}
                <div className={`
                  p-3 rounded-lg flex-shrink-0
                  ${isComplete ? 'bg-green-100 text-green-600' : isUnlocked ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}
                `} data-id="element-471">
                  {isComplete ? <CheckCircle2 className="w-6 h-6" data-id="element-472" /> : <Icon className="w-6 h-6" data-id="element-473" />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0" data-id="element-474">
                  <h3 className={`font-semibold text-lg truncate ${isComplete ? 'text-gray-500' : 'text-gray-900'}`} data-id="element-475">
                    {section.title}
                  </h3>
                  <p className="text-sm text-gray-500 truncate" data-id="element-476">
                    {isComplete ? 'Completed' : section.description}
                  </p>
                </div>

                {/* Action */}
                <div className="flex-shrink-0" data-id="element-477">
                  {isComplete ? <div className="px-4 py-2 text-sm font-medium text-green-600 bg-green-50 rounded-full border border-green-100" data-id="element-478">
                      Done
                    </div> : isUnlocked ? <Button onClick={() => onStartSection(section.id)} className="group" data-id="element-479">
                      Start
                      <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" data-id="element-480" />
                    </Button> : <div className="p-2 text-gray-400" data-id="element-481">
                      <Lock className="w-5 h-5" data-id="element-482" />
                    </div>}
                </div>
              </div>

              {/* Progress bar for active section could go here */}
            </div>;
      })}
      </div>
    </div>;
}